import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { ASSETS, COMPANY, NAV_LINKS } from "@/lib/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const onHero = pathname === "/" && !scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        onHero
          ? "bg-transparent"
          : "bg-white/90 backdrop-blur-md border-b border-[#D4D4CE] shadow-[0_4px_24px_-12px_rgba(14,58,31,0.18)]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-white ring-1 ring-[#D4D4CE] overflow-hidden flex items-center justify-center">
              <img
                src={ASSETS.logo}
                alt="Southern Greens Lawn Care logo"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="leading-tight">
              <div
                className={`font-display font-bold text-sm md:text-base ${
                  onHero ? "text-white" : "text-[#0E3A1F]"
                }`}
              >
                Southern Greens
              </div>
              <div
                className={`text-[10px] md:text-xs tracking-widest uppercase ${
                  onHero ? "text-white/80" : "text-[#4B5563]"
                }`}
              >
                Lawncare &amp; More
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? onHero
                        ? "text-white"
                        : "text-[#0E3A1F]"
                      : onHero
                        ? "text-white/85 hover:text-white"
                        : "text-[#0A0A0A] hover:text-[#0E3A1F]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{l.label}</span>
                    <span
                      className={`absolute left-4 right-4 -bottom-0.5 h-[2px] rounded-full transition-all ${
                        isActive
                          ? onHero
                            ? "bg-[#5BB23B]"
                            : "bg-[#5BB23B]"
                          : "bg-transparent"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={COMPANY.telHref}
              data-testid="header-call-link"
              className={`flex items-center gap-2 text-sm font-semibold ${
                onHero ? "text-white" : "text-[#0E3A1F]"
              }`}
            >
              <Phone className="h-4 w-4" />
              {COMPANY.phoneDisplay}
            </a>
            <Link
              to="/quote"
              data-testid="header-quote-button"
              className="sg-btn-primary px-5 py-2.5 rounded-full text-sm font-semibold"
            >
              Free Quote
            </Link>
          </div>

          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`md:hidden p-2 rounded-lg ${
              onHero ? "text-white" : "text-[#0E3A1F]"
            }`}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu-drawer"
        className={`md:hidden fixed inset-x-0 top-16 transition-all duration-300 ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
      >
        <div className="bg-[#0E3A1F] text-white shadow-2xl border-t border-white/10">
          <div className="px-4 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-base font-semibold ${
                    isActive
                      ? "bg-[#5BB23B] text-white"
                      : "text-white/90 hover:bg-white/10"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={COMPANY.telHref}
              data-testid="mobile-call-link"
              className="mt-3 px-4 py-3 rounded-lg bg-white text-[#0E3A1F] font-bold flex items-center gap-2"
            >
              <Phone className="h-5 w-5" /> Call {COMPANY.phoneDisplay}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
